import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, TrendingUpIcon, AwardIcon, ChevronRightIcon, LockIcon, UnlockIcon, AlertCircleIcon, InfoIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { mockFacilities } from '../mocks/financingData';
type FinancingState = 'ineligible' | 'pre-approved' | 'active-repayment' | 'top-up-unlocked';
export function Financing() {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<FinancingState>('top-up-unlocked');
  const [borrowAmount, setBorrowAmount] = useState<number>(130000);
  const MAX_AMOUNT = 150000;
  const MIN_AMOUNT = 10000;
  const FACTOR_RATE = 1.15; // Gold tier
  const HOLDBACK_PERCENT = 10;
  const totalRepayment = borrowAmount * FACTOR_RATE;
  const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
    currency: 'MYR'
  });
  return <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4623">
      {/* Dev State Toggle (for prototype demonstration) */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-4624">
        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-4625">
          <InfoIcon size={16} data-id="element-4626" />
          <span data-id="element-4627">Prototype State:</span>
        </div>
        <div className="flex gap-2" data-id="element-4628">
          {(['ineligible', 'pre-approved', 'active-repayment', 'top-up-unlocked'] as FinancingState[]).map(s => <button key={s} onClick={() => setViewState(s)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewState === s ? 'bg-[var(--brand-primary)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4629">
              {s.replace('-', ' ').toUpperCase()}
            </button>)}
        </div>
      </div>

      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-id="element-4630">
        <div data-id="element-4631">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-4632">Financing</h1>
          <p className="text-gray-500 mt-2" data-id="element-4633">
            Flexible, data-driven capital to grow your business.
          </p>
        </div>
        <button onClick={() => navigate('/bills/financing/apply')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-lg font-semibold transition-colors shadow-sm self-start sm:self-auto" data-id="element-4634">
          <TrendingUpIcon size={18} data-id="element-4635" />
          Apply for financing
        </button>
      </div>

      {/* BLOCK 1 & 2: Offer & Progress */}
      {viewState === 'ineligible' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8" data-id="element-4636">
          <div className="max-w-2xl" data-id="element-4637">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4638">
              Unlock {BRAND.name} Financing
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-4639">
              You're on your way to unlocking pre-approved capital. Keep
              processing with us to build your business profile and access
              instant funding.
            </p>

            <div className="space-y-6" data-id="element-4640">
              <div data-id="element-4641">
                <div className="flex justify-between text-sm mb-2" data-id="element-4642">
                  <span className="font-medium text-gray-700" data-id="element-4643">
                    Time on platform
                  </span>
                  <span className="text-gray-900" data-id="element-4644">4 / 6 months</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4645">
                  <div className="h-full bg-[var(--brand-primary)] w-[66%]" data-id="element-4646" />
                </div>
              </div>
              <div data-id="element-4647">
                <div className="flex justify-between text-sm mb-2" data-id="element-4648">
                  <span className="font-medium text-gray-700" data-id="element-4649">
                    Monthly Volume
                  </span>
                  <span className="text-gray-900" data-id="element-4650">MYR 20k / 50k</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4651">
                  <div className="h-full bg-[var(--brand-primary)] w-[40%]" data-id="element-4652" />
                </div>
              </div>
              <div data-id="element-4653">
                <div className="flex justify-between text-sm mb-2" data-id="element-4654">
                  <span className="font-medium text-gray-700" data-id="element-4655">
                    Chargeback Rate
                  </span>
                  <span className="text-green-600 font-medium" data-id="element-4656">
                    0.1% (Excellent)
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4657">
                  <div className="h-full bg-green-500 w-[100%]" data-id="element-4658" />
                </div>
              </div>
            </div>
          </div>
        </div>}

      {viewState === 'pre-approved' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-4659">
          {/* Left Column */}
          <div className="flex flex-col justify-center" data-id="element-4660">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4661">
              Pre-Qualified Loan Offer
            </h2>
            <p className="text-lg text-gray-600 mb-8" data-id="element-4662">
              A personalised, fully-priced offer the moment you cross the
              eligibility threshold. No application form — accept in one tap.
            </p>
            <ul className="space-y-4" data-id="element-4663">
              {['Offers refresh every 30 days based on the latest PSP signals', 'Pricing is locked at acceptance — no surprises', 'Funds disbursed to settlement account within 24 hours', 'Repayment automatic — nothing to remember'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-4664">
                  <CheckCircle2Icon className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0" size={20} data-id="element-4665" />
                  <span className="text-gray-700" data-id="element-4666">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Right Column: Hero Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden relative" data-id="element-4667">
            <div className="absolute top-0 left-0 w-full h-1 bg-[var(--brand-primary)]" data-id="element-4668" />
            <div className="p-8" data-id="element-4669">
              <div className="flex items-center justify-between mb-8" data-id="element-4670">
                <div className="flex items-center gap-3" data-id="element-4671">
                  <img src={BRAND.logoUrl} alt={BRAND.name} className="h-8 object-contain" data-id="element-4672" />
                  <span className="text-sm font-medium text-gray-500 border-l pl-3 border-gray-300" data-id="element-4673">
                    Lending
                  </span>
                </div>
                <span className="px-2.5 py-1 bg-red-50 text-[var(--brand-primary)] text-xs font-bold tracking-wide rounded-full" data-id="element-4674">
                  NEW
                </span>
              </div>

              <div className="mb-2 text-sm font-bold tracking-wider text-gray-500 uppercase" data-id="element-4675">
                YOU'RE PRE-APPROVED
              </div>
              <div className="text-gray-600 mb-1" data-id="element-4676">Borrow up to</div>
              <div className="text-4xl font-bold text-gray-900 mb-8" data-id="element-4677">
                {formatMYR(MAX_AMOUNT)}
              </div>

              <div className="mb-8" data-id="element-4678">
                <div className="flex justify-between text-sm font-medium mb-4" data-id="element-4679">
                  <span className="text-gray-700" data-id="element-4680">Choose amount</span>
                  <span className="text-[var(--brand-primary)] text-lg" data-id="element-4681">
                    {formatMYR(borrowAmount)}
                  </span>
                </div>
                <input type="range" min={MIN_AMOUNT} max={MAX_AMOUNT} step={1000} value={borrowAmount} onChange={e => setBorrowAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--brand-primary)]" data-id="element-4682" />
                <div className="flex justify-between text-xs text-gray-400 mt-2" data-id="element-4683">
                  <span data-id="element-4684">{formatMYR(MIN_AMOUNT)}</span>
                  <span data-id="element-4685">{formatMYR(MAX_AMOUNT)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-4686">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-4687">
                  <div className="text-xs text-gray-500 font-medium mb-1" data-id="element-4688">
                    TOTAL REPAYMENT
                  </div>
                  <div className="text-lg font-bold text-gray-900" data-id="element-4689">
                    {formatMYR(totalRepayment)}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-4690">
                  <div className="text-xs text-gray-500 font-medium mb-1" data-id="element-4691">
                    HOLDBACK
                  </div>
                  <div className="text-lg font-bold text-gray-900" data-id="element-4692">
                    {HOLDBACK_PERCENT}% daily
                  </div>
                </div>
              </div>

              <button onClick={() => navigate('/bills/financing/apply')} className="w-full py-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold text-lg transition-colors shadow-md shadow-red-500/20" data-id="element-4693">
                Accept & Get Funded
              </button>
              <p className="text-center text-xs text-gray-500 mt-4" data-id="element-4694">
                Funds arrive within 24 hours · Pricing locked at acceptance
              </p>
            </div>
          </div>
        </div>}

      {(viewState === 'active-repayment' || viewState === 'top-up-unlocked') && <div className="space-y-6" data-id="element-4695">
          {/* Compact Active Facility Summary */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center justify-between" data-id="element-4696">
            <div data-id="element-4697">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-4698">
                Current Facility
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-4699">
                {formatMYR(130000)}
              </div>
            </div>
            <div className="text-right" data-id="element-4700">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-4701">
                Holdback Rate
              </div>
              <div className="text-lg font-semibold text-gray-900" data-id="element-4702">10%</div>
            </div>
          </div>

          {/* Top-Up Progress Block */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4703">
            <div className="p-6 border-b border-gray-100" data-id="element-4704">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-4705">
                Top-Up Progress
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100" data-id="element-4706">
              <div className={`p-8 ${viewState === 'top-up-unlocked' ? 'md:col-span-2' : 'md:col-span-3'}`} data-id="element-4707">
                <div className="flex justify-between items-end mb-4" data-id="element-4708">
                  <div data-id="element-4709">
                    <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-4710">
                      Repaid so far
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-id="element-4711">
                      {formatMYR(101660)}{' '}
                      <span className="text-lg text-gray-400 font-normal" data-id="element-4712">
                        / {formatMYR(149500)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-4713">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-sm font-semibold" data-id="element-4714">
                      <TrendingUpIcon size={14} data-id="element-4715" />
                      68% repaid
                    </span>
                  </div>
                </div>

                <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-6" data-id="element-4716">
                  <div className="h-full bg-[var(--brand-primary)] w-[68%]" data-id="element-4717" />
                </div>

                <div className="flex justify-between text-sm text-gray-500" data-id="element-4718">
                  <span data-id="element-4719">Started · Jan 2026</span>
                  <span data-id="element-4720">Projected close · Oct 2026</span>
                </div>
              </div>

              {viewState === 'top-up-unlocked' && <div className="p-8 bg-gray-50 flex flex-col justify-center relative overflow-hidden" data-id="element-4721">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-red-100 rounded-bl-full -mr-8 -mt-8" data-id="element-4722" />
                  <div className="flex items-center gap-2 mb-4" data-id="element-4723">
                    <UnlockIcon size={18} className="text-[var(--brand-primary)]" data-id="element-4724" />
                    <span className="text-sm font-bold tracking-wider text-[var(--brand-primary)] uppercase" data-id="element-4725">
                      Top-Up Unlocked
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4726">
                    {formatMYR(170000)}
                  </div>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-4727">
                    Re-modelled offer based on your last 90 days. Score improved
                    from 81.7 → 84.2.
                  </p>
                  <button onClick={() => navigate('/bills/financing/apply')} className="w-full py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-900 rounded-lg font-medium transition-colors shadow-sm" data-id="element-4728">
                    See new offer
                  </button>
                </div>}
            </div>
          </div>
        </div>}

      {/* BLOCK 2.5: Your Facilities — hidden when the merchant is ineligible (no facilities yet) */}
      {viewState !== 'ineligible' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4729">
          <div className="p-6 border-b border-gray-100" data-id="element-4730">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-4731">Your Facilities</h2>
          </div>
          {mockFacilities.length === 0 ? <div className="p-8 text-center text-gray-500" data-id="element-4732">
              No active financing
            </div> : <div className="overflow-x-auto" data-id="element-4733">
              <table className="w-full text-left border-collapse" data-id="element-4734">
                <thead data-id="element-4735">
                  <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500" data-id="element-4736">
                    <th className="px-6 py-4 font-medium" data-id="element-4737">Facility Ref</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4738">Principal</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4739">Total Repayment</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4740">Outstanding</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4741">Repaid</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4742">Holdback</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4743">Timeline</th>
                    <th className="px-6 py-4 font-medium" data-id="element-4744">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-4745">
                  {mockFacilities.map(facility => <tr key={facility.id} onClick={() => navigate(`/bills/financing/facility/${facility.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-4746">
                      <td className="px-6 py-4" data-id="element-4747">
                        <div className="font-medium text-gray-900 group-hover:text-[var(--brand-primary)] transition-colors" data-id="element-4748">
                          {facility.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600" data-id="element-4749">
                        {formatMYR(facility.principal)}
                      </td>
                      <td className="px-6 py-4 text-gray-600" data-id="element-4750">
                        {formatMYR(facility.totalRepayment)}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-4751">
                        {formatMYR(facility.outstanding)}
                      </td>
                      <td className="px-6 py-4" data-id="element-4752">
                        <div className="flex items-center gap-2" data-id="element-4753">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4754">
                            <div className="h-full bg-[var(--brand-primary)]" style={{
                      width: `${facility.repaidPercent}%`
                    }} data-id="element-4755" />
                          </div>
                          <span className="text-sm text-gray-600" data-id="element-4756">
                            {facility.repaidPercent}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600" data-id="element-4757">
                        {facility.holdbackRate}%
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-4758">
                        {facility.startDate} – {facility.projectedCloseDate}
                      </td>
                      <td className="px-6 py-4" data-id="element-4759">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${facility.status === 'Active' ? 'bg-green-50 text-green-700' : facility.status === 'In arrears' ? 'bg-red-50 text-red-700' : facility.status === 'Closing soon' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-4760">
                          {facility.status}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}

      {/* BLOCK 3: CIMB Rewards */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4761">
        <div className="p-8 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-4762">
          <div data-id="element-4763">
            <div className="flex items-center gap-2 mb-2" data-id="element-4764">
              <AwardIcon size={24} className="text-[var(--brand-primary)]" data-id="element-4765" />
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-4766">
                {BRAND.name} Rewards
              </h2>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-4767">
              Better business → Better rates
            </h3>
            <p className="text-gray-600 max-w-2xl" data-id="element-4768">
              Earn points by maintaining healthy business signals. Every tier
              reduces your pricing on the next loan or top-up.
            </p>
          </div>
          <div className="bg-gray-50 px-6 py-4 rounded-xl border border-gray-200 text-center min-w-[160px]" data-id="element-4769">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-4770">
              Your Points
            </div>
            <div className="text-3xl font-bold text-[var(--brand-primary)]" data-id="element-4771">
              700
            </div>
          </div>
        </div>

        <div className="p-8" data-id="element-4772">
          <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-6" data-id="element-4773">
            Rate Ladder
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4774">
            {[{
            tier: 'Bronze',
            rate: '×1.22',
            status: 'unlocked'
          }, {
            tier: 'Silver',
            rate: '×1.18',
            status: 'unlocked'
          }, {
            tier: 'Gold',
            rate: '×1.15',
            status: 'active'
          }, {
            tier: 'Platinum',
            rate: '×1.11',
            status: 'locked'
          }].map(t => <div key={t.tier} className={`relative p-5 rounded-xl border-2 transition-all ${t.status === 'active' ? 'border-[var(--brand-primary)] bg-red-50/30' : t.status === 'unlocked' ? 'border-gray-200 bg-white opacity-60' : 'border-gray-100 bg-gray-50 opacity-50'}`} data-id="element-4775">
                {t.status === 'active' && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[var(--brand-primary)] text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-sm" data-id="element-4776">
                    YOU
                  </div>}
                <div className="flex justify-between items-center mb-3" data-id="element-4777">
                  <span className={`font-bold ${t.status === 'active' ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-4778">
                    {t.tier}
                  </span>
                  {t.status === 'locked' && <LockIcon size={14} className="text-gray-400" data-id="element-4779" />}
                  {t.status === 'unlocked' && <CheckCircle2Icon size={14} className="text-gray-400" data-id="element-4780" />}
                </div>
                <div className={`text-2xl font-bold ${t.status === 'active' ? 'text-[var(--brand-primary)]' : 'text-gray-400'}`} data-id="element-4781">
                  {t.rate}
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-4782">Factor rate</div>
              </div>)}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-4783">
            <AlertCircleIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4784" />
            <p className="text-sm text-blue-800" data-id="element-4785">
              Earn <strong data-id="element-4786">250 more points</strong> to unlock Platinum and save{' '}
              <strong data-id="element-4787">{formatMYR(6000)}</strong> on your next MYR 150k loan.
            </p>
          </div>
        </div>
      </div>
    </div>;
}